import React, { useEffect, useRef, useState } from 'react';
import TickBox from '../components/TickBox';
import Loader from '../../Loader/Loader';
import { logoUrl } from '../runtime';
import { usePlaylistVideos } from '../hooks/usePlaylistVideos';
import { ytReady, ytOrigin } from '../hooks/ytApi';
const thumbOf = id => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

// Rebuilt on every open rather than reusing loadVideoById: a fresh instance is
// the only reliable way to reset a portrait player between reels.
const ReelModal = ({
  videoId,
  title,
  checked,
  blocked,
  onToggle,
  onClose
}) => {
  const mountRef = useRef(null);
  useEffect(() => {
    if (!videoId) return undefined;
    const mount = mountRef.current;
    let alive = true;
    let player = null;
    ytReady(() => {
      if (!alive || !mount) return;
      mount.innerHTML = '';
      const host = document.createElement('div');
      host.style.width = '100%';
      host.style.height = '100%';
      mount.appendChild(host);
      player = new window.YT.Player(host, {
        width: '100%',
        height: '100%',
        host: 'https://www.youtube-nocookie.com',
        videoId,
        playerVars: {
          autoplay: 1,
          rel: 0,
          playsinline: 1,
          modestbranding: 1,
          ...ytOrigin()
        },
        events: {
          // Plays with sound: the modal only opens from a tap, so the browser
          // has the activation unmuted playback needs.
          onReady: e => {
            e.target.unMute();
            e.target.playVideo();
          }
        }
      });
    });
    return () => {
      alive = false;
      if (player) {
        try {
          player.destroy();
        } catch (e) {
          /* already gone */
        }
      }
      if (mount) mount.innerHTML = '';
    };
    // Only the id rebuilds the player. Ticking the reel re-renders the parent
    // and hands down a new onClose, which would otherwise restart playback.
  }, [videoId]);
  useEffect(() => {
    const onKey = e => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!videoId) return null;
  return React.createElement("div", {
    className: "cp-reelscrim",
    onClick: onClose
  }, React.createElement("div", {
    className: "cp-reelmodal",
    onClick: e => e.stopPropagation()
  }, React.createElement("div", {
    className: "cp-reelmodal-media"
  }, React.createElement("div", {
    ref: mountRef,
    className: "cp-playermount"
  }), React.createElement("button", {
    type: "button",
    className: "cp-reelmodal-close",
    onClick: onClose,
    "aria-label": "Close"
  }, React.createElement("svg", {
    width: "15",
    height: "15",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    strokeLinecap: "round"
  }, React.createElement("path", {
    d: "M18 6L6 18M6 6l12 12"
  })))), React.createElement("button", {
    type: "button",
    className: `cp-reelmodal-tick${checked ? ' is-on' : ''}`,
    disabled: blocked,
    onClick: onToggle
  }, React.createElement(TickBox, {
    checked: checked
  }), React.createElement("span", null, checked ? 'Selected' : 'Select this reel')), React.createElement("p", {
    className: "cp-reelmodal-title"
  }, title)));
};

/**
 * A field whose single option is a source rather than a value: the client
 * picks individual videos out of that playlist, so the answer holds YouTube
 * video ids.
 */
const ReelPicker = ({
  field,
  picked,
  locked,
  onToggle
}) => {
  const source = (field.options || [])[0];
  const [openId, setOpenId] = useState(null);

  // The answer holds video ids. A source option's own id is never one of them,
  // but older answers stored it back when this field rendered as a plain
  // picker — drop it, or it counts against the cap for good.
  const sourceIds = (field.options || []).map(o => String(o.id));
  const stale = (picked || []).filter(x => sourceIds.includes(String(x)));
  useEffect(() => {
    if (locked) return;
    stale.forEach(id => onToggle(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stale.join(',')]);
  let listId = null;
  try {
    listId = source ? new URL(source.embed_url).searchParams.get('list') : null;
  } catch (e) {
    listId = null;
  }
  const {
    ids,
    titles,
    failed
  } = usePlaylistVideos(listId);
  const cap = field.max_picks || 0;
  const chosen = (picked || []).filter(x => !sourceIds.includes(String(x)));
  const atLimit = cap > 0 && chosen.length >= cap;
  const isOn = id => chosen.some(x => String(x) === String(id));
  if (!source) return null;

  // The API never arrived, so the playlist cannot be enumerated. Show it
  // whole rather than an empty grid.
  if (failed || !listId && source.embed_url) {
    return React.createElement("div", {
      className: "cp-higrid is-solo"
    }, React.createElement("div", {
      className: "cp-playerbox"
    }, React.createElement("div", {
      className: "cp-player"
    }, React.createElement("iframe", {
      title: source.name,
      src: source.embed_url,
      allow: "accelerometer; autoplay; encrypted-media; picture-in-picture",
      allowFullScreen: true
    }))));
  }
  if (!ids.length) {
    return React.createElement(Loader, {
      inline: true,
      logoSrc: logoUrl(),
      label: "Loading reels"
    });
  }
  return React.createElement(React.Fragment, null, React.createElement("div", {
    className: "cp-reelgrid"
  }, ids.map((id, i) => {
    const on = isOn(id);
    const capped = !on && atLimit;
    const title = titles[id] || `Reel ${i + 1}`;
    return React.createElement("div", {
      key: id,
      className: `cp-reel${on ? ' is-on' : ''}${capped ? ' is-capped' : ''}${locked && !on ? ' is-locked' : ''}`
    }, React.createElement("button", {
      type: "button",
      className: "cp-reel-media",
      onClick: () => setOpenId(id),
      "aria-label": `Play ${title}`
    },
    // An image rather than a background, so a long playlist only fetches
    // the thumbnails the client has scrolled near.
    React.createElement("img", {
      className: "cp-reel-thumb",
      src: thumbOf(id),
      alt: "",
      loading: "lazy",
      decoding: "async"
    }), React.createElement("span", {
      className: "cp-reel-scrim"
    }), React.createElement("span", {
      className: "cp-reel-play"
    }, React.createElement("svg", {
      width: "15",
      height: "15",
      viewBox: "0 0 24 24",
      fill: "#FFFFFF"
    }, React.createElement("path", {
      d: "M8 5l12 7-12 7z"
    })))), React.createElement("button", {
      type: "button",
      className: `cp-tickrow${on ? ' is-on' : ''}${capped ? ' is-capped' : ''}`,
      disabled: locked || capped,
      onClick: () => onToggle(id),
      "aria-label": `${on ? 'Deselect' : 'Select'} ${title}`
    }, React.createElement(TickBox, {
      checked: on
    }), React.createElement("span", {
      className: "cp-reel-title"
    }, title)));
  })), React.createElement(ReelModal, {
    videoId: openId,
    title: openId ? titles[openId] || 'Reel' : '',
    checked: openId ? isOn(openId) : false,
    blocked: locked || (openId ? !isOn(openId) && atLimit : false),
    onToggle: () => openId && onToggle(openId),
    onClose: () => setOpenId(null)
  }));
};
export default ReelPicker;
